/**
 * Chat contract — the wire shapes shared by the dashboard server
 * (`ChatRepo` + the `/api/chat/*` routes + the realtime `chat_message`
 * push) and the web client (the Chat page + `RealtimeProvider`). Single
 * source of truth so the two sides never disagree about message shapes,
 * the room id, or how a conversation is addressed.
 *
 * The chat is **internal staff team chat**: 1:1 direct messages between
 * staff PLUS one built-in broadcast room ("All Staff") that everyone is in
 * (where `@channel` — see {@link CHAT_MENTION_ALL} — notifies every member).
 * It rides the existing realtime WebSocket for live delivery (a new
 * `chat_message` server frame) but mutations (send / mark-read) go over
 * plain REST — that keeps validation + error handling conventional and
 * leaves the socket's client→server protocol (status / ping) untouched.
 *
 * Read state is tracked with a per-viewer, per-conversation **read cursor**
 * (highest message id the viewer has seen), NOT a per-message `read_at`,
 * because the broadcast room has many recipients — a single `read_at`
 * column could never represent "who has read this". See docs/chat.md.
 */

import type { CallEndReason } from "./calls.js";

/** The built-in broadcast room every staff member is in. */
export const CHAT_ROOM_ALL = "all";
export type ChatRoomId = typeof CHAT_ROOM_ALL;

/** Human label for the broadcast room (sidebar + thread header). */
export const CHAT_ROOM_ALL_LABEL = "All Staff";

/**
 * Reserved `sender_id` for automated SYSTEM / bot chat messages — e.g. the
 * "XOP Updates" desktop-release announcement the server posts to All Staff.
 * Real `dashboard_users` ids are always positive, so `0` can never collide
 * with a person; and because edit/delete are scoped to `sender_id`, no user
 * can ever tamper with a system post. The client detects a system message
 * purely by `sender_id === CHAT_SYSTEM_SENDER_ID` — no schema / wire-shape
 * change. NOTE: this is a plain canned message, NOT routed through the Otto
 * AI/LLM — it's just a bot voice in the staff room. See docs/chat.md.
 */
export const CHAT_SYSTEM_SENDER_ID = 0;

/** Display name stamped on automated system / bot chat messages. */
export const CHAT_SYSTEM_SENDER_NAME = "XOP Updates";

/** True when a message was posted by the reserved automated system sender
 * (the client renders it with a "BOT" badge + the XOP mark instead of a
 * person's initials avatar). */
export function isSystemChatMessage(m: Pick<ChatMessage, "sender_id">): boolean {
  return m.sender_id === CHAT_SYSTEM_SENDER_ID;
}

/**
 * Compose the "XOP Updates" All-Staff announcement body for a new desktop
 * release. **No changelog** — every signed-in staff member (incl. testers /
 * support) reads this room, so it stays a short, actionable "update the app"
 * note; the "what's new" detail lives in the Change Log, deliberately not here.
 *
 * Shared (not server-only) so the producer (`desktopReleaseAnnouncer`) and the
 * consumer (the chat thread's "Update now" action) agree on the exact wording
 * — the `(vX.Y.Z)` token is the stable anchor {@link
 * desktopReleaseAnnouncementVersion} parses back out. The round-trip is pinned
 * in chat.test.ts.
 */
export function buildDesktopReleaseMessage(version: string): string {
  return (
    `📢 A new version of the XOP Command Center desktop app (v${version}) is now available.\n\n` +
    `On the desktop app, click “Update now” below — or open the tray menu and ` +
    `choose “Check for Updates…”.`
  );
}

/** Matches the desktop-release announcement body built by {@link
 * buildDesktopReleaseMessage}, capturing the advertised version. The
 * `(vX.Y.Z)` token (digits/dots, plus an optional `-prerelease` suffix) is the
 * stable contract both sides agree on. */
const DESKTOP_RELEASE_ANNOUNCEMENT_RE = /desktop app \(v([\w.-]+)\) is now available/;

/**
 * If `m` is the automated desktop-release announcement — a system message (the
 * reserved "XOP Updates" bot) whose body advertises a new desktop version —
 * return the advertised version string; otherwise `null`.
 *
 * Lets the chat thread attach an "Update now" action to *exactly* that one
 * message **without a schema / wire-shape change** (the same design choice as
 * {@link isSystemChatMessage}). Symmetric with {@link buildDesktopReleaseMessage}.
 */
export function desktopReleaseAnnouncementVersion(
  m: Pick<ChatMessage, "sender_id" | "body">,
): string | null {
  if (!isSystemChatMessage(m)) return null;
  const match = DESKTOP_RELEASE_ANNOUNCEMENT_RE.exec(m.body);
  return match?.[1] ?? null;
}

/**
 * One-line call record posted into the DM thread when a 1:1 call ends, so the
 * conversation keeps a log of calls. The 📞 prefix is the stable marker the
 * chat thread uses to render it as a call event:
 *   - "completed" (answered)        → "📞 Call ended · m:ss" (with duration)
 *   - "declined"                    → "📞 Call declined"
 *   - "failed" (couldn't connect)   → "📞 Call failed"
 *   - "missed" / "cancelled" / else → "📞 Missed call"
 * Pinned in chat.test.ts.
 */
export function buildCallRecordMessage(reason: CallEndReason, durationMs: number): string {
  if (reason === "completed") {
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `📞 Call ended · ${minutes}:${seconds}`;
  }
  if (reason === "declined") return "📞 Call declined";
  if (reason === "failed") return "📞 Call failed";
  return "📞 Missed call";
}

/** Max characters in a single chat message body. */
export const CHAT_MESSAGE_MAX_LENGTH = 4000;

/** Max characters in the one-line preview of a quoted (replied-to) message —
 * the snippet shown inside a reply bubble. A bound keeps the quote a single
 * tidy line regardless of how long the original was. */
export const CHAT_REPLY_PREVIEW_MAX = 120;

/** Max number of @mentions stamped on a single message (server-enforced cap so
 * one message can't fan out an unbounded notification storm). Does NOT bound
 * {@link CHAT_MENTION_ALL_ID} — that fan-out is derived server-side from the
 * roster, not supplied by the client, so it can't be used to amplify. */
export const CHAT_MENTION_MAX = 20;

/**
 * Reserved `mentions[].user_id` for `@channel` — "notify EVERY staff member",
 * the All-Staff-room analogue of an @mention. Real `dashboard_users` ids are
 * always positive (and `0` is the reserved system sender), so `-1` can never
 * collide with a person — the same sentinel-id trick as
 * {@link CHAT_SYSTEM_SENDER_ID}, and likewise **no schema / wire-shape change**:
 * one sentinel entry rides the existing `mentions` JSON column.
 *
 * The message stamps exactly ONE sentinel mention; the server expands it to the
 * whole roster (minus the sender) at send time to fire the notifications. It is
 * **room-only** — a "notify everyone" token inside a 1:1 is meaningless, so the
 * send route drops it on a DM. See docs/chat.md → "@channel".
 */
export const CHAT_MENTION_ALL_ID = -1;

/** Display name stamped on the `@channel` sentinel mention — the text after the
 * `@` in the body, so {@link segmentChatText} chips it with no extra rules. */
export const CHAT_MENTION_ALL_NAME = "channel";

/** The `@channel` sentinel as a ready-to-use mention (composer candidate +
 * the entry stamped on the row). */
export const CHAT_MENTION_ALL: ChatMention = {
  user_id: CHAT_MENTION_ALL_ID,
  display_name: CHAT_MENTION_ALL_NAME,
};

/** True when a mention is the `@channel` "notify everyone" sentinel rather than
 * a real teammate. The one predicate every surface tests — never compare the
 * raw id inline. */
export function isMentionAll(m: Pick<ChatMention, "user_id">): boolean {
  return m.user_id === CHAT_MENTION_ALL_ID;
}

/** True when a message carries the `@channel` sentinel (so the client can treat
 * its chip as concerning the viewer regardless of who they are). */
export function mentionsEveryone(m: Pick<ChatMessage, "mentions">): boolean {
  return (m.mentions ?? []).some(isMentionAll);
}

/** A chat message scope: a 1:1 direct message, or a broadcast room. */
export type ChatScope = "dm" | "room";

/** The fixed palette of emoji a staff member can react to a message with.
 * A small curated set keeps the picker simple and the column values bounded.
 * Shared so the server validates against the same list the picker renders. */
export const CHAT_REACTION_EMOJI = ["👍", "❤️", "😂", "🎉", "🙏", "👀"] as const;
export type ChatReactionEmoji = (typeof CHAT_REACTION_EMOJI)[number];

/** True when `v` is one of the allowed reaction emoji (route-input guard). */
export function isChatReactionEmoji(v: unknown): v is ChatReactionEmoji {
  return typeof v === "string" && (CHAT_REACTION_EMOJI as readonly string[]).includes(v);
}

/** One emoji's reactions on a message, aggregated. `user_ids` lets each
 * viewer derive "did I react" from their own id — so the same shape serves
 * the REST response AND the realtime broadcast (no per-viewer computation).
 *
 * `user_names` is the display name of each reactor, positionally aligned with
 * `user_ids` (index i names user_ids[i]) — it feeds the "who reacted" hover
 * tooltip on the reaction chip. Optional so an older payload (or a hand-built
 * test fixture) stays valid; the client falls back to a bare count. */
export interface ChatReactionSummary {
  emoji: string;
  count: number;
  user_ids: number[];
  user_names?: string[];
}

/** Max bytes for a single uploaded attachment (client pre-check; the server
 * enforces its own configurable limit too). 100 MB — large enough for short
 * videos / screen recordings; the aggregate per-user + total caps still bound
 * disk use so the box can't overflow. */
export const CHAT_MAX_FILE_BYTES = 100 * 1024 * 1024;

/**
 * A file attached to a chat message — the SAFE, client-facing view. The
 * on-disk storage key is NEVER exposed; the file is only ever readable
 * through the auth-gated `GET /api/chat/attachments/:id` endpoint, which
 * checks the caller is a participant. See docs/chat.md.
 */
export interface ChatAttachment {
  id: string;
  filename: string;
  mime: string;
  size_bytes: number;
  /**
   * True for a **recorded voice message** (mic in the composer), as opposed to
   * an audio FILE the user attached. Both are `audio/*`, but a voice note
   * renders as a WhatsApp-style waveform bubble (play / scrub / speed), while a
   * plain audio file keeps the file/player chip. Set at upload time from the
   * `x-voice-note` header; emitted only when true. See docs/chat.md.
   */
  voice_note?: boolean;
}

/** MIME types we render inline as an image preview; everything else is a
 * download-only file chip (so an uploaded .svg/.html can't execute). */
const PREVIEWABLE_IMAGE_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export function isPreviewableImageMime(mime: string | null | undefined): boolean {
  return !!mime && PREVIEWABLE_IMAGE_MIMES.has(mime.toLowerCase());
}

/** Video MIME types chat plays inline with a `<video controls>` element
 * (the browser-supported set). */
const PLAYABLE_VIDEO_MIMES = new Set([
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
]);

/** Audio MIME types chat plays inline with an `<audio controls>` element. */
const PLAYABLE_AUDIO_MIMES = new Set([
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/webm",
  "audio/aac",
  "audio/mp4",
  "audio/x-m4a",
  "audio/m4a",
]);

export function isPlayableVideoMime(mime: string | null | undefined): boolean {
  return !!mime && PLAYABLE_VIDEO_MIMES.has(mime.toLowerCase());
}

export function isPlayableAudioMime(mime: string | null | undefined): boolean {
  return !!mime && PLAYABLE_AUDIO_MIMES.has(mime.toLowerCase());
}

/** How a chat attachment renders inline in the thread: an image preview, an
 * in-app `<video>` / `<audio>` player, or a download-only file chip. The chat
 * UI plays media itself (auth-gated blob → object URL) — it never hands the
 * file off to a new browser tab. */
export type ChatAttachmentKind = "image" | "video" | "audio" | "file";

export function chatAttachmentKind(mime: string | null | undefined): ChatAttachmentKind {
  if (isPreviewableImageMime(mime)) return "image";
  if (isPlayableVideoMime(mime)) return "video";
  if (isPlayableAudioMime(mime)) return "audio";
  return "file";
}

/**
 * Whether an attachment is a recorded voice message (renders as the WhatsApp
 * waveform bubble) rather than an attached audio file. A voice note is always
 * playable audio, but the inverse isn't true — an uploaded `.mp3` is audio,
 * not a voice note. The render branch checks this FIRST, then falls back to
 * `chatAttachmentKind`. See docs/chat.md.
 */
export function isVoiceNoteAttachment(att: ChatAttachment | null | undefined): boolean {
  return !!att && att.voice_note === true;
}

/** Human-readable byte size, e.g. "2.4 MB". */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "0 B";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let val = n / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val < 10 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

/** Strip path separators / control chars and cap the length of an uploaded
 * filename (display + Content-Disposition safety). Server + client share it. */
export function sanitizeAttachmentFilename(raw: string): string {
  const cleaned = (raw || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[/\\]+/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  const safe = cleaned.slice(0, 200);
  return safe || "file";
}

/**
 * A teammate @-mentioned in a message. Stamped on the message at send time
 * (id + the target's display name at that moment), so the highlight renders
 * without a per-message user lookup and survives a later rename. Drives both
 * the rendered chip and (for the notification) who gets pinged.
 */
export interface ChatMention {
  user_id: number;
  display_name: string;
}

/**
 * One run of message text. `"link"` is an external URL (opens in a new tab);
 * `"applink"` is an internal entity deep link rendered as in-app navigation
 * (no new tab) so a shared lead/prospect/customer opens inside the SPA — on
 * web AND in the Tauri desktop app. `"mention"` is an `@teammate` reference
 * rendered as a highlighted chip. See docs/chat.md + shareChat.ts.
 */
export interface ChatTextSegment {
  type: "text" | "link" | "applink" | "mention";
  value: string;
  /** Present (and always http/https) when `type === "link"`. */
  href?: string;
  /** Present when `type === "mention"` — the mentioned user's id. */
  userId?: number;
}

// External links: only http(s):// and www. — never javascript:/data:, so a
// linkified href can't be an XSS vector.
const URL_SRC = "(?:https?://|www\\.)[^\\s<>]+";

// Internal entity deep links we make clickable — a PRECISE allowlist of the
// shareable detail routes (sales lead, CRM prospect / customer / AI prospect,
// documentation file), so we never linkify arbitrary slashes a user happens to
// type. The negative lookbehind keeps the SAME path from matching when it sits
// *inside* an absolute URL (e.g. https://host/crm/customers/1) — the URL
// alternative wins there and the applink one is suppressed. Kept in sync with
// the share-message builders' `deepLinkPath` (shareChat.ts + the documentation
// share subject).
const APP_LINK_SRC =
  "(?<![\\w./])/(?:sales/leads|crm/(?:prospects|customers|ai-prospects)|documentation/library)/[\\w-]+";

// Applink alternative FIRST (with its lookbehind); the URL alternative is tried
// at the same position when the applink one fails. A URL never starts with "/"
// and an applink always does, so `m[0][0] === "/"` discriminates the two.
const SEGMENT_PATTERN = new RegExp(`${APP_LINK_SRC}|${URL_SRC}`, "gi");

/**
 * Split a message body into plain-text + link runs (WhatsApp-style autolink),
 * recognizing both external URLs (`"link"`) and internal entity deep links
 * (`"applink"`). Trailing sentence punctuation is kept out of an external link,
 * and a bare `www.` host is given an `https://` href.
 *
 * When `mentions` is supplied (the message's stamped `@teammate` list) a second
 * pass splits each remaining text run on `@${display_name}` spans into
 * `"mention"` segments — longest name first, so a fuller name wins over a
 * shorter one and a name that's the prefix of a longer word (`@Bob` in
 * `@Bobby`) never matches. Callers that pass no mentions get the link-only
 * behaviour verbatim. One choke point for all chat text rendering.
 */
export function segmentChatText(
  text: string,
  mentions?: readonly ChatMention[],
): ChatTextSegment[] {
  const linkSegs = segmentLinks(text);
  if (!mentions || mentions.length === 0) return linkSegs;

  const tokens = mentions
    .map((m) => ({ token: `@${m.display_name}`, userId: m.user_id }))
    .sort((a, b) => b.token.length - a.token.length);

  const out: ChatTextSegment[] = [];
  for (const seg of linkSegs) {
    if (seg.type !== "text") {
      out.push(seg);
      continue;
    }
    out.push(...splitMentions(seg.value, tokens));
  }
  return out.length ? out : [{ type: "text", value: text }];
}

/** The external-URL / internal-applink segmentation (mention-agnostic). */
function segmentLinks(text: string): ChatTextSegment[] {
  const out: ChatTextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(SEGMENT_PATTERN)) {
    const start = m.index ?? 0;
    const isAppLink = m[0].startsWith("/");
    let raw = m[0];
    let trailing = "";
    if (!isAppLink) {
      const trail = raw.match(/[.,!?;:'")\]}]+$/);
      trailing = trail ? trail[0] : "";
      if (trailing) raw = raw.slice(0, raw.length - trailing.length);
    }
    if (!raw) continue;
    if (start > last) out.push({ type: "text", value: text.slice(last, start) });
    if (isAppLink) {
      out.push({ type: "applink", value: raw });
    } else {
      out.push({ type: "link", value: raw, href: raw.startsWith("http") ? raw : `https://${raw}` });
    }
    if (trailing) out.push({ type: "text", value: trailing });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out.length ? out : [{ type: "text", value: text }];
}

/** Greedy left-to-right scan of a text run, emitting a `"mention"` segment for
 * each `@${display_name}` token (longest-first) at a word boundary. */
function splitMentions(
  text: string,
  tokens: readonly { token: string; userId: number }[],
): ChatTextSegment[] {
  const isWord = (c: string | undefined): boolean => !!c && /\w/.test(c);
  const out: ChatTextSegment[] = [];
  let buf = "";
  let i = 0;
  while (i < text.length) {
    let hit: { token: string; userId: number } | null = null;
    for (const t of tokens) {
      if (
        text.startsWith(t.token, i) &&
        !isWord(text[i - 1]) &&
        !isWord(text[i + t.token.length])
      ) {
        hit = t;
        break;
      }
    }
    if (hit) {
      if (buf) {
        out.push({ type: "text", value: buf });
        buf = "";
      }
      out.push({ type: "mention", value: hit.token, userId: hit.userId });
      i += hit.token.length;
    } else {
      buf += text[i];
      i += 1;
    }
  }
  if (buf) out.push({ type: "text", value: buf });
  return out;
}

/**
 * A quoted preview of the message a reply is "in reply to" (WhatsApp-style).
 * Attached to the replying message at READ time by resolving the original row
 * (a self-join, no per-message lookup), so it always reflects the original's
 * current state — crucially, it honours the tombstone: if the original was
 * since deleted, `preview` is blank and `deleted` is true, and the quote never
 * leaks deleted content. `id` powers "jump to the original" in the thread.
 */
export interface ChatReplyPreview {
  /** id of the quoted (original) message — used to scroll to it on click. */
  id: number;
  /** The original's sender (so the quote can label it "You" vs a name). */
  sender_id: number;
  /** The original sender's display name (resolved live). */
  sender_name: string;
  /** One-line snippet of the original (truncated body or a media label).
   * Empty when the original was deleted (render the tombstone instead). */
  preview: string;
  /** True when the quoted original has since been deleted — the client shows
   * a "deleted message" placeholder rather than the (blank) preview. */
  deleted?: boolean;
}

/**
 * Build the one-line preview snippet shown inside a reply's quote block: the
 * trimmed/truncated body, or a media label for a file-only message. Returns
 * "" when there's nothing to show (the caller renders a tombstone for a
 * deleted original). Shared so the server stamps the same snippet the composer
 * shows while drafting the reply.
 */
export function buildReplyPreview(opts: {
  body: string | null | undefined;
  attachmentMime?: string | null;
  attachmentFilename?: string | null;
}): string {
  const body = (opts.body ?? "").trim();
  if (body) {
    return body.length > CHAT_REPLY_PREVIEW_MAX
      ? `${body.slice(0, CHAT_REPLY_PREVIEW_MAX - 1)}…`
      : body;
  }
  if (opts.attachmentMime) {
    const kind = chatAttachmentKind(opts.attachmentMime);
    if (kind === "image") return "📷 Photo";
    if (kind === "video") return "🎬 Video";
    if (kind === "audio") return "🎤 Voice message";
    return `📎 ${(opts.attachmentFilename || "File").trim()}`;
  }
  return "";
}

/**
 * One persisted chat message (wire shape). Sender identity is stamped at
 * send time — chat logs legitimately capture who-said-what — so the thread
 * renders a name without a per-message user lookup.
 */
export interface ChatMessage {
  id: number;
  scope: ChatScope;
  /** Room id for `scope:"room"` (always "all" today); null for a DM. */
  room: ChatRoomId | null;
  sender_id: number;
  sender_name: string;
  /** The other participant for `scope:"dm"`; null for a room message. */
  recipient_id: number | null;
  body: string;
  /** A file attached to this message (download-gated), or null. */
  attachment: ChatAttachment | null;
  created_at: string; // ISO-8601
  /** Aggregated emoji reactions, one entry per distinct emoji (empty / absent
   * when none). Optional so older constructors + the realtime guard stay
   * lenient; the client treats a missing value as "no reactions". */
  reactions?: ChatReactionSummary[];
  /** ISO timestamp of the last body edit, or null/absent if never edited
   * (drives the "edited" label). */
  edited_at?: string | null;
  /** ISO timestamp of a soft-delete, or null/absent if live. When set, the
   * server blanks `body`/`attachment` — the client renders a tombstone. */
  deleted_at?: string | null;
  /** Teammates @-mentioned in this message (empty / absent when none).
   * Stamped at send time; drives the highlighted chips and the mention
   * notification. Optional so older constructors + the realtime guard stay
   * lenient. */
  mentions?: ChatMention[];
  /** When this message is a reply, a live-resolved quote of the original it
   * replies to (null/absent for a non-reply, or when the reply itself is a
   * tombstone). Optional so older constructors + the realtime guard stay
   * lenient. */
  reply_to?: ChatReplyPreview | null;
}

/** A target conversation, addressed by the client. */
export type ChatTarget =
  | { kind: "room"; room: ChatRoomId }
  | { kind: "dm"; user_id: number };

/**
 * A staff member in the roster, with THIS viewer's unread count for the
 * 1:1 conversation with them. Presence (online / away / dnd) is merged in
 * on the client from the realtime channel, not stored here.
 */
export interface ChatContact {
  user_id: number;
  email: string;
  display_name: string;
  unread: number;
  /** ISO timestamp of the most recent message in this DM, or null if none. */
  last_message_at: string | null;
  /** Body of the most recent message in this DM (for the list preview). */
  last_message: string | null;
  /** True when the most recent message was sent BY the viewer ("You: …"). */
  last_message_from_me: boolean;
}

/** The built-in broadcast room, with this viewer's unread count. */
export interface ChatRoomSummary {
  room: ChatRoomId;
  label: string;
  unread: number;
  last_message_at: string | null;
  /** Body + sender name of the most recent room message (list preview). */
  last_message: string | null;
  last_message_sender: string | null;
}

/** `GET /api/chat/overview` — the sidebar: the room + the full staff roster. */
export interface ChatOverviewResponse {
  self_id: number;
  room: ChatRoomSummary;
  contacts: ChatContact[];
  total_unread: number;
  /** Server-wide chat attachment storage usage, so the team can see how
   * much of the cap is used and never overflow the box. */
  storage: ChatStorageUsage;
}

/** Total bytes used by chat attachments + the configured cap. */
export interface ChatStorageUsage {
  used_bytes: number;
  max_bytes: number;
}

/** `GET /api/chat/conversations/:target` — a page of message history. */
export interface ChatConversationResponse {
  messages: ChatMessage[];
  /** True when older messages exist before the first returned one. */
  has_more: boolean;
}

/** `POST /api/chat/messages` body. */
export interface SendChatMessageRequest {
  /** "all" for the broadcast room, or a recipient user id for a DM. */
  to: ChatRoomId | number;
  body: string;
  /** Optional attachment id (from `POST /api/chat/attachments`). When set,
   * `body` may be empty (a file with no caption). */
  attachment_id?: string;
  /** User ids @-mentioned in the body (from the composer picker). The server
   * validates each is a real, conversation-participant user, drops the sender,
   * and caps at {@link CHAT_MENTION_MAX} before stamping + notifying. */
  mention_ids?: number[];
  /** id of the message this one replies to (WhatsApp-style quote). The server
   * validates it exists, is live (not deleted), and belongs to THIS same
   * conversation (a reply can never quote a message from another DM or pull a
   * DM into the room). Stored as a back-reference; the quote is resolved on
   * read. */
  reply_to?: number;
}

/** `POST /api/chat/messages` response — the created message + new unread total. */
export interface SendChatMessageResponse {
  message: ChatMessage;
  total_unread: number;
}

/** `POST /api/chat/conversations/:target/read` response. */
export interface MarkChatReadResponse {
  ok: true;
  total_unread: number;
}

/** `PATCH /api/chat/messages/:id` (edit) body. Sender-only; sets `edited_at`. */
export interface EditChatMessageRequest {
  body: string;
}

/** Response for edit (`PATCH`) and delete (`DELETE`) of a message — the full
 * updated message (a delete comes back blanked into a tombstone). */
export interface ChatMessageMutationResponse {
  message: ChatMessage;
}

/** `POST /api/chat/messages/:id/reactions` body + response. The caller sends
 * one emoji to toggle (add if absent, remove if present); the response is the
 * message's full new aggregated reaction set. */
export interface ToggleChatReactionRequest {
  emoji: ChatReactionEmoji;
}
export interface ToggleChatReactionResponse {
  message_id: number;
  reactions: ChatReactionSummary[];
}

/**
 * The conversation key used by the server's read cursor AND the client's
 * react-query cache: `"room:all"` or `"dm:<otherUserId>"`. Stable per
 * (viewer, conversation) so both sides agree.
 */
export function chatConversationKey(target: ChatTarget): string {
  return target.kind === "room" ? `room:${target.room}` : `dm:${target.user_id}`;
}

/**
 * Parse a URL path segment ("all" | "<userId>") into a {@link ChatTarget}.
 * Returns null for anything else so a route can 400 instead of trusting it.
 */
export function parseChatTarget(raw: string): ChatTarget | null {
  if (raw === CHAT_ROOM_ALL) return { kind: "room", room: CHAT_ROOM_ALL };
  if (/^\d+$/.test(raw)) {
    const id = Number.parseInt(raw, 10);
    return Number.isSafeInteger(id) && id > 0 ? { kind: "dm", user_id: id } : null;
  }
  return null;
}

/** Normalize the `to` field of a send request into a {@link ChatTarget}. */
export function targetFromSendField(to: unknown): ChatTarget | null {
  if (to === CHAT_ROOM_ALL) return { kind: "room", room: CHAT_ROOM_ALL };
  if (typeof to === "number" && Number.isSafeInteger(to) && to > 0) {
    return { kind: "dm", user_id: to };
  }
  if (typeof to === "string") return parseChatTarget(to);
  return null;
}

/**
 * Normalize + validate a message body. Returns the trimmed body, or an
 * error message when invalid (empty / too long / not a string).
 */
export function validateChatBody(
  raw: unknown,
): { ok: true; body: string } | { ok: false; error: string } {
  if (typeof raw !== "string") return { ok: false, error: "body must be a string" };
  const body = raw.trim();
  if (!body) return { ok: false, error: "body must not be empty" };
  if (body.length > CHAT_MESSAGE_MAX_LENGTH) {
    return { ok: false, error: `body exceeds ${CHAT_MESSAGE_MAX_LENGTH} characters` };
  }
  return { ok: true, body };
}

/**
 * Best-effort human name from an email local-part — the fallback the roster
 * uses when a user has no stored display name yet (e.g. never signed in
 * since the `name` column shipped). "j.smits.work@xop.com" → "J Smits Work".
 */
export function displayNameFromEmail(email: string): string {
  const local = (email.split("@")[0] ?? "").trim();
  const words = local.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return email;
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

/**
 * Runtime guard for an untrusted {@link ChatMessage} (used by the realtime
 * server-frame parser before it's handed to the client cache).
 */
export function isChatMessage(v: unknown): v is ChatMessage {
  if (typeof v !== "object" || v === null) return false;
  const m = v as Record<string, unknown>;
  return (
    typeof m.id === "number" &&
    (m.scope === "dm" || m.scope === "room") &&
    (m.room === null || typeof m.room === "string") &&
    typeof m.sender_id === "number" &&
    typeof m.sender_name === "string" &&
    (m.recipient_id === null || typeof m.recipient_id === "number") &&
    typeof m.body === "string" &&
    (m.attachment == null || typeof m.attachment === "object") &&
    typeof m.created_at === "string"
  );
}
